import { AGGREGATION_TYPE, GRAPH_PERIODICITY } from './enums/generic.enum';

export interface IQuaterlySetObj {
  iterations: number;
  value: number;
}

export interface IYearSetObj {
  iterations: number;
  value: number;
  quarterObj: Map<number, IQuaterlySetObj>;
}

export interface IGighChartGroupedCategories {
  name: string;
  categories: Array<any>;
}

export function getQuarterOfTheYear(date): number {
  const datObj = new Date(
    new Date(date).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  );
  const month = datObj.getMonth() + 1;
  return Math.ceil(month / 3);
}

export function getLastMonthOfQuarterByDate(date): number {
  const quarter = getQuarterOfTheYear(date);
  return quarter * 3 - 1; // - 1 because on send the index of the month 0 - 11
}

function getYear(date): number {
  const datObj = new Date(
    new Date(date).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  );
  return datObj.getFullYear();
}

function loadPeriodicityData(
  aggrType: AGGREGATION_TYPE,
  objArray: Array<any>,
  attr: string
): Map<number, IYearSetObj> {
  const dataMap = new Map<number, IYearSetObj>();
  objArray.forEach((element) => {
    const year = getYear(element.METRICS_DATETIME);
    if (!dataMap.get(year)) {
      dataMap.set(year, {
        iterations: 0,
        value: 0,
        quarterObj: new Map(),
      });
    }

    const yearObj = dataMap.get(year);

    if (attr) {
      if (aggrType === AGGREGATION_TYPE.AVG) {
        yearObj.value =
          (yearObj.value * yearObj.iterations + element[attr]) / (yearObj.iterations + 1);
      } else {
        yearObj.value += element[attr];
      }
    }

    yearObj.iterations += 1;

    const quaterly = getQuarterOfTheYear(element.METRICS_DATETIME);

    if (!yearObj.quarterObj.get(quaterly)) {
      yearObj.quarterObj.set(quaterly, {
        iterations: 0,
        value: 0,
      });
    }

    const quaterlyObj = yearObj.quarterObj.get(quaterly);
    if (attr) {
      if (aggrType === AGGREGATION_TYPE.AVG) {
        quaterlyObj.value =
          (quaterlyObj.value * quaterlyObj.iterations + element[attr]) /
          (quaterlyObj.iterations + 1);
      } else {
        quaterlyObj.value += element[attr];
      }
    }

    quaterlyObj.iterations += 1;

    yearObj.quarterObj.set(quaterly, quaterlyObj);
    dataMap.set(year, yearObj);
  });
  return dataMap;
}

export function getDatesFromData(data: Array<any>, periodicity: GRAPH_PERIODICITY): any {
  if (periodicity !== GRAPH_PERIODICITY.MONTHLY) {
    const yearSet = loadPeriodicityData(AGGREGATION_TYPE.SUM, data, null);

    if (periodicity === GRAPH_PERIODICITY.YEARLY) {
      return Array.from(yearSet.keys());
    }

    if (periodicity === GRAPH_PERIODICITY.QUATERLY) {
      const quaterlyArray = [];
      Array.from(yearSet.keys()).forEach((e) => {
        Array.from(yearSet.get(e).quarterObj.keys()).forEach((qObj) => {
          quaterlyArray.push('' + e + ' Q' + qObj);
        });
      });
      return quaterlyArray;
    }
  }
  return data.map((a) =>
    new Date(a.METRICS_DATETIME).toLocaleString('en-GB', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  );
}

export function getValuesFromData(
  objArray: Array<any>,
  attr: string,
  periodicity: GRAPH_PERIODICITY,
  aggrType?: AGGREGATION_TYPE
): Array<any> {
  if (!aggrType || periodicity === GRAPH_PERIODICITY.MONTHLY) {
    return objArray.map((a) => (a[attr]));
  }

  const yearSet = loadPeriodicityData(aggrType, objArray, attr);

  if (periodicity === GRAPH_PERIODICITY.YEARLY) {
    return Array.from(yearSet.values()).map((a) => a.value);
  }

  // Quaterly
  const quaterlyArray = [];
  Array.from(yearSet.values()).forEach((e) => {
    Array.from(e.quarterObj.values()).forEach((qObj) => {
      quaterlyArray.push(qObj.value);
    });
  });
  return quaterlyArray;
}

export function getCategoriesByYear(
  objArray: Array<any>,
  periodicity: GRAPH_PERIODICITY
): Array<IGighChartGroupedCategories> {
  const response: Array<IGighChartGroupedCategories> = [];
  if (periodicity === GRAPH_PERIODICITY.QUATERLY) {
    objArray.forEach((element) => {
      const year = getYear(element.METRICS_DATETIME);
      const quaterly = getQuarterOfTheYear(element.METRICS_DATETIME);
      const yearIndex = response.findIndex((e) => e.name === year.toString());
      if (yearIndex === -1) {
        response.push({
          name: year.toString(),
          categories: [quaterly],
        });
      } else {
        const quarterlyIndex = response[yearIndex].categories.findIndex((e) => e === quaterly);
        if (quarterlyIndex === -1) {
          response[yearIndex].categories.push(quaterly);
        }
      }
    });
    return response;
  }
}

export function getFormattedNumber(value: number | string) {
  if (value < 0) {
    return `(${Math.abs(Number(value)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })})`;
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function getLastDayAndTimeOfMonth(date: Date): Date {
  const lastMomentofQuarter = new Date(
    date.getFullYear(),
    getLastMonthOfQuarterByDate(date) + 1,
    0
  );
  lastMomentofQuarter.setHours(23, 59, 59, 999);

  return lastMomentofQuarter;
}
